import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

/**
 * Shared pricing-card component for the /pricing/vendors and /pricing/clubs
 * pages (iter57). Handles three visual variants:
 *   - `default`   → cream/white card
 *   - `featured`  → dark green MOST POPULAR card
 *   - `premium`   → cream/white with subtle gold accent border (Featured Vendor)
 *
 * The wax-seal logo (`/logo.png`) sits near the bottom of every card to
 * reinforce membership in the My Crest Club network, per spec.
 */
export default function PricingCard({ tier, variant = "default", ctaTo, testId }) {
  const isFeatured = variant === "featured";
  const isPremium = variant === "premium";

  const shellCls = isFeatured
    ? "bg-[#1A422D] text-[#F9F8F4] border-[#1A422D]"
    : isPremium
      ? "bg-white text-[#1A1A1A] border-[#D9C68A] ring-1 ring-[#D9C68A]/50"
      : "bg-white text-[#1A1A1A] border-[#E5E3D8]";

  const eyebrowCls = isFeatured ? "text-[#D9C68A]" : "text-[#B59A5A]";
  const priceCls = isFeatured ? "text-[#F9F8F4]" : "text-[#1A422D]";
  const bodyCls = isFeatured ? "text-[#F9F8F4]/85" : "text-[#4A4A4A]";
  const checkCls = isFeatured ? "text-[#D9C68A]" : "text-[#1A422D]";

  const ctaCls = isFeatured
    ? "bg-[#D9C68A] text-[#1A422D] hover:bg-[#E5D6A8]"
    : isPremium
      ? "bg-[#1A422D] text-[#F9F8F4] hover:bg-[#0F2A1C] ring-1 ring-[#D9C68A]/60"
      : "bg-[#1A422D] text-[#F9F8F4] hover:bg-[#0F2A1C]";

  return (
    <div
      data-testid={testId}
      className={`relative flex flex-col p-8 lg:p-10 border transition-all duration-300 hover:-translate-y-1 ${shellCls}`}
    >
      {isFeatured && (
        <div
          data-testid={`${testId}-badge`}
          className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-[#D9C68A] text-[#1A422D] text-[10px] uppercase tracking-[0.28em] font-semibold whitespace-nowrap"
        >
          Most Popular
        </div>
      )}

      <div className={`text-[11px] uppercase tracking-[0.28em] font-semibold ${eyebrowCls}`}>
        {tier.eyebrow}
      </div>
      <h3 className={`mt-4 font-serif text-2xl lg:text-3xl leading-tight ${priceCls}`}>
        {tier.name}
      </h3>
      <div className={`mt-6 font-serif text-4xl lg:text-5xl leading-none tracking-tight ${priceCls}`}>
        {tier.price_display}
      </div>
      <p className={`mt-6 text-sm leading-relaxed ${bodyCls}`}>{tier.tagline}</p>

      <ul className={`mt-8 space-y-2.5 text-sm ${bodyCls}`}>
        {(tier.features || []).map((f) => (
          <li key={f} className="flex gap-2.5 items-start">
            <Check size={15} className={`${checkCls} mt-1 shrink-0`} strokeWidth={2.2} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="flex-1" />

      <div className="mt-10">
        <Link
          to={ctaTo}
          data-testid={`${testId}-cta`}
          className={`inline-flex items-center justify-center w-full gap-2 px-6 py-3.5 text-[12px] uppercase tracking-[0.24em] font-semibold transition-colors ${ctaCls}`}
        >
          {tier.cta_label} <span aria-hidden>→</span>
        </Link>
      </div>

      {/* Wax-seal logo — small, centered, reinforces network membership */}
      <div className="mt-8 flex justify-center" aria-hidden>
        <img
          src="/logo.png"
          alt=""
          className={`h-10 w-auto ${isFeatured ? "opacity-90" : "opacity-70"}`}
        />
      </div>

      <p className={`mt-6 text-xs leading-relaxed text-center ${isFeatured ? "text-[#F9F8F4]/70" : "text-[#7A7A7A]"}`}>
        {tier.footer_line}
      </p>
    </div>
  );
}
